import { PointerEvent as ReactPointerEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { fmtDate } from '@/features/backlog/format';
import { cn } from './cn';
import { inputCls } from './form';
import { Calendar, Chevron } from './icons';

/** Parse a "YYYY-MM-DD" value into local year/month/day parts (no tz drift). */
const parseISO = (v: string | null | undefined): { y: number; m: number; d: number } | null => {
  if (!v) return null;
  const [y, m, d] = v.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
};

/** Format local year/month/day back into a zero-padded "YYYY-MM-DD" string. */
const toISO = (y: number, m: number, d: number): string =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const isWeekend = (dow: number) => dow === 0 || dow === 6; // Sunday / Saturday
// Monday-first week so the weekend (Sat, Sun) sits in the last two columns.
const WEEK: { label: string; dow: number }[] = [
  { label: 'Mo', dow: 1 },
  { label: 'Tu', dow: 2 },
  { label: 'We', dow: 3 },
  { label: 'Th', dow: 4 },
  { label: 'Fr', dow: 5 },
  { label: 'Sa', dow: 6 },
  { label: 'Su', dow: 0 },
];

// Popover footprint (7×32px day cells + gaps + padding + border), used to place/flip it
// before it paints. Width is exact so the day grid never wraps or overflows.
// Sized so the 7-day grid holds ~44px (touch-friendly) day cells while still fitting a phone.
const PANEL_W = 340;
const PANEL_H = 420;
const GAP = 4; // gap between trigger and popover

type Cell = { y: number; m: number; d: number; dow: number; inMonth: boolean };

/** 6×7 grid of days for the given month (month0 is 0-based), padded with adjacent days. */
const buildMonth = (year: number, month0: number): Cell[] => {
  const dow0 = new Date(year, month0, 1).getDay(); // weekday of the 1st (0=Sun)
  const startOffset = (dow0 + 6) % 7; // days back to the Monday that starts the 1st's week
  const cells: Cell[] = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(year, month0, 1 - startOffset + i);
    cells.push({
      y: dt.getFullYear(),
      m: dt.getMonth(),
      d: dt.getDate(),
      dow: dt.getDay(),
      inMonth: dt.getMonth() === month0 && dt.getFullYear() === year,
    });
  }
  return cells;
};

const stop = (e: ReactPointerEvent) => e.stopPropagation(); // keep it from starting a card/row drag

type Props = {
  value: string | null; // "YYYY-MM-DD"
  onChange: (v: string | null) => void;
  /** Small heading inside the popover naming what is being picked (e.g. "Start date"). */
  heading?: string;
  /** Custom trigger; falls back to an input-styled button showing the formatted date. */
  trigger?: (o: { open: boolean; toggle: () => void; label: string }) => ReactNode;
  align?: 'left' | 'right';
  /** Gray out and block Saturday/Sunday (default true). */
  disableWeekends?: boolean;
  /** Show a Clear action when a value is set (default true). */
  clearable?: boolean;
  placeholder?: string;
  /** Extra classes for the default trigger button. */
  triggerClassName?: string;
  'aria-label'?: string;
};

export function DatePicker({
  value,
  onChange,
  heading,
  trigger,
  align = 'left',
  disableWeekends = true,
  clearable = true,
  placeholder = 'Select date',
  triggerClassName,
  'aria-label': ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [view, setView] = useState(() => {
    const p = parseISO(value);
    const base = p ? new Date(p.y, p.m - 1, 1) : new Date();
    return { y: base.getFullYear(), m0: base.getMonth() };
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Position the popover in the viewport (fixed), flipping above the trigger when there isn't
  // room below. Rendered in a portal so no `overflow` ancestor (kanban columns row, modal body)
  // can clip it — matching the browser's old native-picker top-layer behavior.
  const place = () => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const up = spaceBelow < PANEL_H + GAP && r.top > spaceBelow;
    const rawTop = up ? r.top - GAP - PANEL_H : r.bottom + GAP;
    const rawLeft = align === 'right' ? r.right - PANEL_W : r.left;
    setPos({
      top: Math.max(8, Math.min(rawTop, window.innerHeight - PANEL_H - 8)),
      left: Math.max(8, Math.min(rawLeft, window.innerWidth - PANEL_W - 8)),
    });
  };

  // Place before paint whenever open, and keep it anchored as the page/containers scroll or resize.
  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align]);

  // Close on outside click / Escape. Escape stops propagation so it dismisses only the calendar,
  // not a surrounding Modal (which listens for Escape on window).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openCal = () => {
    // Re-sync the visible month to the current value each time it opens.
    const p = parseISO(value);
    const base = p ? new Date(p.y, p.m - 1, 1) : new Date();
    setView({ y: base.getFullYear(), m0: base.getMonth() });
    setOpen(true);
  };
  const toggle = () => (open ? setOpen(false) : openCal());

  const sel = parseISO(value);
  const now = new Date();
  const today = { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
  const cells = buildMonth(view.y, view.m0);
  const monthLabel = new Date(view.y, view.m0, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const shift = (delta: number) =>
    setView((v) => {
      const dt = new Date(v.y, v.m0 + delta, 1);
      return { y: dt.getFullYear(), m0: dt.getMonth() };
    });

  const pick = (c: Cell) => {
    const iso = toISO(c.y, c.m + 1, c.d);
    if (iso !== (value ? value.slice(0, 10) : null)) onChange(iso); // avoid a redundant save on re-pick
    setOpen(false);
  };
  const clear = () => {
    onChange(null);
    setOpen(false);
  };

  const label = fmtDate(value);

  return (
    <div ref={rootRef} className="relative" onPointerDown={stop} onClick={(e) => e.stopPropagation()}>
      {trigger ? (
        trigger({ open, toggle, label })
      ) : (
        <button
          type="button"
          onClick={toggle}
          aria-label={ariaLabel}
          className={cn(inputCls, 'flex items-center justify-between gap-2 text-left', !value && 'text-fg-ghost', triggerClassName)}
        >
          <span className="truncate">{label || placeholder}</span>
          <Calendar className="shrink-0 text-fg-faint" />
        </button>
      )}

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              onPointerDown={stop}
              style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W }}
              className="z-[2100] rounded-xl border border-line bg-surface-raised p-2.5 shadow-2xl shadow-black/60"
            >
              {heading ? (
                <div className="mb-2 border-b border-line pb-2 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">
                  {heading}
                </div>
              ) : null}

              {/* Month header + navigation */}
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => shift(-1)}
                  className="tap-target flex h-6 w-6 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer hover:text-fg"
                  aria-label="Previous month"
                >
                  <Chevron className="rotate-180" />
                </button>
                <span className="select-none text-[13px] font-semibold text-fg">{monthLabel}</span>
                <button
                  type="button"
                  onClick={() => shift(1)}
                  className="tap-target flex h-6 w-6 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer hover:text-fg"
                  aria-label="Next month"
                >
                  <Chevron />
                </button>
              </div>

              {/* Weekday header (Monday-first; weekend in the last two columns) */}
              <div className="grid grid-cols-7 gap-0.5">
                {WEEK.map((w) => (
                  <span
                    key={w.label}
                    className={cn(
                      'flex h-8 w-full items-center justify-center text-[11px] font-semibold uppercase',
                      disableWeekends && isWeekend(w.dow) ? 'text-fg-ghost' : 'text-fg-faint',
                    )}
                  >
                    {w.label}
                  </span>
                ))}
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((c) => {
                  const weekend = disableWeekends && isWeekend(c.dow);
                  const selected = !!sel && sel.y === c.y && sel.m === c.m + 1 && sel.d === c.d;
                  const isToday = today.y === c.y && today.m === c.m && today.d === c.d;
                  return (
                    <button
                      key={`${c.y}-${c.m}-${c.d}`}
                      type="button"
                      disabled={weekend}
                      onClick={() => pick(c)}
                      aria-label={fmtDate(toISO(c.y, c.m + 1, c.d))}
                      aria-current={isToday ? 'date' : undefined}
                      className={cn(
                        'flex h-11 w-full items-center justify-center rounded-md text-[13px] transition-colors',
                        selected
                          ? 'bg-brand font-semibold text-white'
                          : weekend
                            ? 'cursor-not-allowed text-fg-ghost'
                            : cn('hover:bg-layer', c.inMonth ? 'text-fg-soft' : 'text-fg-faint'),
                        !selected && weekend && !c.inMonth && 'opacity-40',
                        // Today: clear accent ring (+ accent text when it's a selectable day).
                        !selected && isToday && 'ring-1 ring-inset ring-accent',
                        !selected && !weekend && isToday && 'font-semibold text-accent',
                      )}
                    >
                      {c.d}
                    </button>
                  );
                })}
              </div>

              {clearable && value ? (
                <div className="mt-2 flex items-center justify-end border-t border-line pt-2">
                  <button type="button" onClick={clear} className="flex min-h-[44px] items-center rounded-md px-2 text-[11px] font-medium text-fg-muted transition-colors hover:bg-layer hover:text-fg">
                    Clear
                  </button>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
